import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import UserStatsCard from './components/UserStatsCard';
import UserFilters from './components/UserFilters';
import UserTable from './components/UserTable';
import UserModal from './components/UserModal';
import SecurityPanel from './components/SecurityPanel';

const UserManagement = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedUser, setSelectedUser] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [filters, setFilters] = useState({
    search: '',
    department: 'all',
    role: 'all',
    status: 'all'
  });

  // Mock user data
  const [users, setUsers] = useState([
    {
      id: 1,
      name: "Kadiatou Bah",
      email: "kadiatou.bah@ecomotech.com",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      department: "Engineering",
      role: "admin",
      status: "active",
      phone: "+224 620 123 456",
      jobTitle: "Senior Software Engineer",
      lastLogin: new Date(Date.now() - 2 * 60 * 60 * 1000),
      permissions: ["read_users", "write_users", "system_admin"]
    },
    {
      id: 2,
      name: "Thierno Sow",
      email: "thierno.sow@ecomotech.com",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      department: "Sales",
      role: "manager",
      status: "active",
      phone: "+224 621 234 567",
      jobTitle: "Sales Manager",
      lastLogin: new Date(Date.now() - 5 * 60 * 60 * 1000),
      permissions: ["read_users", "read_reports"]
    },
    {
      id: 3,
      name: "Mariama Condé",
      email: "mariama.conde@ecomotech.com",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      department: "Marketing",
      role: "employee",
      status: "active",
      phone: "+224 622 345 678",
      jobTitle: "Marketing Specialist",
      lastLogin: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      permissions: ["read_reports"]
    },
    {
      id: 4,
      name: "Alsény Sylla",
      email: "alseny.sylla@ecomotech.com",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      department: "Finance",
      role: "manager",
      status: "inactive",
      phone: "+224 623 456 789",
      jobTitle: "Finance Manager",
      lastLogin: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      permissions: ["read_users", "read_reports", "write_reports"]
    },
    {
      id: 5,
      name: "Hawa Bangoura",
      email: "hawa.bangoura@ecomotech.com",
      avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
      department: "HR",
      role: "manager",
      status: "active",
      phone: "+224 624 567 890",
      jobTitle: "HR Manager",
      lastLogin: new Date(Date.now() - 3 * 60 * 60 * 1000),
      permissions: ["read_users", "write_users", "delete_users"]
    },
    {
      id: 6,
      name: "Sékou Keïta",
      email: "sekou.keita@ecomotech.com",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
      department: "Operations",
      role: "employee",
      status: "suspended",
      phone: "+224 625 678 901",
      jobTitle: "Operations Coordinator",
      lastLogin: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
      permissions: ["read_reports"]
    },
    {
      id: 7,
      name: "Aminata Diakité",
      email: "aminata.diakite@ecomotech.com",
      avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face",
      department: "Engineering",
      role: "employee",
      status: "active",
      phone: "+224 626 789 012",
      jobTitle: "Frontend Developer",
      lastLogin: new Date(Date.now() - 30 * 60 * 1000),
      permissions: ["read_users"]
    },
    {
      id: 8,
      name: "Mohamed Cissé",
      email: "mohamed.cisse@ecomotech.com",
      avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face",
      department: "Sales",
      role: "employee",
      status: "pending",
      phone: "+224 627 890 123",
      jobTitle: "Sales Representative",
      lastLogin: null,
      permissions: []
    }
  ]);

  // Mock security data
  const securityData = {
    failedLogins: 12,
    activeSessions: 24,
    passwordResets: 8,
    suspiciousActivities: 3,
    accountLockouts: 2,
    twoFactorEnabled: 15,
    recentAlerts: [
      {
        id: 1,
        type: "critical",
        title: "Multiple Failed Login Attempts",
        description: "User account locked due to 5 consecutive failed login attempts",
        timestamp: new Date(Date.now() - 30 * 60 * 1000),
        user: "ousmane.diallo@ecomotech.com",
        ip: "192.168.1.100"
      },
      {
        id: 2,
        type: "warning",
        title: "Unusual Login Location",
        description: "Login detected from new geographic location",
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        user: "kadiatou.bah@ecomotech.com",
        ip: "41.223.45.67"
      },
      {
        id: 3,
        type: "info",
        title: "Password Reset Request",
        description: "User requested password reset via email verification",
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
        user: "thierno.sow@ecomotech.com",
        ip: "41.223.100.23"
      }
    ],
    recentSessions: [
      {
        id: 1,
        user: "Kadiatou Bah",
        location: "Conakry, Guinea",
        device: "Chrome on Windows",
        lastActive: new Date(Date.now() - 15 * 60 * 1000)
      },
      {
        id: 2,
        user: "Thierno Sow",
        location: "Labé, Guinea",
        device: "Safari on macOS",
        lastActive: new Date(Date.now() - 45 * 60 * 1000)
      },
      {
        id: 3,
        user: "Mariama Condé",
        location: "Kankan, Guinea",
        device: "Firefox on Linux",
        lastActive: new Date(Date.now() - 2 * 60 * 60 * 1000)
      }
    ]
  };

  // Filter and sort users
  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(filters?.search?.toLowerCase());
    const matchesDepartment = filters?.department === 'all' || user?.department?.toLowerCase() === filters?.department;
    const matchesRole = filters?.role === 'all' || user?.role === filters?.role;
    const matchesStatus = filters?.status === 'all' || user?.status === filters?.status;
    
    return matchesSearch && matchesDepartment && matchesRole && matchesStatus;
  })?.sort((a, b) => {
    const aValue = a?.[sortConfig?.key];
    const bValue = b?.[sortConfig?.key];
    
    if (sortConfig?.direction === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // Calculate stats
  const stats = {
    totalUsers: users?.length,
    activeUsers: users?.filter(u => u?.status === 'active')?.length,
    inactiveUsers: users?.filter(u => u?.status === 'inactive')?.length,
    pendingUsers: users?.filter(u => u?.status === 'pending')?.length
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      department: 'all',
      role: 'all',
      status: 'all'
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectUser = (userId, checked) => {
    setSelectedUsers(prev => 
      checked 
        ? [...prev, userId]
        : prev?.filter(id => id !== userId)
    );
  };

  const handleSelectAll = (checked, currentUsers) => {
    if (checked) {
      const allIds = currentUsers?.map(user => user?.id);
      setSelectedUsers(prev => [...new Set([...prev, ...allIds])]);
    } else {
      const currentIds = currentUsers?.map(user => user?.id);
      setSelectedUsers(prev => prev?.filter(id => !currentIds?.includes(id)));
    }
  };

  const handleBulkAction = (action, userIds) => {
    console.log(`Performing ${action} on users:`, userIds);
    // Implement bulk actions here
    setSelectedUsers([]);
  };

  const handleAddUser = () => {
    setModalMode('add');
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleViewUser = (user) => {
    console.log('Viewing user:', user);
    // Implement user view logic
  };

  const handleResetPassword = (user) => {
    console.log('Resetting password for:', user?.name);
    // Implement password reset logic
  };

  const handleToggleStatus = (user) => {
    const newStatus = user?.status === 'active' ? 'inactive' : 'active';
    setUsers(prev => prev?.map(u => 
      u?.id === user?.id ? { ...u, status: newStatus } : u
    ));
  };

  const handleSaveUser = async (userData) => {
    if (modalMode === 'add') {
      const newUser = {
        ...userData,
        id: Math.max(...users?.map(u => u?.id)) + 1,
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
        lastLogin: null
      };
      setUsers(prev => [...prev, newUser]);
    } else {
      setUsers(prev => prev?.map(u => 
        u?.id === selectedUser?.id ? { ...u, ...userData } : u
      ));
    }
  };

  const handleViewAuditLog = () => {
    console.log('Opening audit log');
    // Implement audit log view
  };

  const handleViewSessions = () => {
    console.log('Opening session management');
    // Implement session management
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={handleSidebarToggle} 
      />
      <div className={`transition-smooth ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <Header sidebarCollapsed={sidebarCollapsed} />
        
        <main className="pt-16 p-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb />
            
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">User Management</h1>
                <p className="text-muted-foreground">
                  Manage user accounts, roles, and permissions across the ERP system
                </p>
              </div>
              <div className="flex items-center space-x-3 mt-4 sm:mt-0">
                <Button
                  variant="outline"
                  onClick={() => setActiveTab(activeTab === 'users' ? 'security' : 'users')}
                  iconName={activeTab === 'users' ? 'Shield' : 'Users'}
                  iconPosition="left"
                >
                  {activeTab === 'users' ? 'Security' : 'Users'}
                </Button>
                <Button
                  onClick={handleAddUser}
                  iconName="UserPlus"
                  iconPosition="left"
                >
                  Add User
                </Button>
              </div>
            </div>

            {activeTab === 'users' ? (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <UserStatsCard
                    title="Total Users"
                    value={stats?.totalUsers}
                    change="+2 this week"
                    changeType="positive"
                    icon="Users"
                    color="primary"
                  />
                  <UserStatsCard
                    title="Active Users"
                    value={stats?.activeUsers}
                    change="+5 this month"
                    changeType="positive"
                    icon="UserCheck"
                    color="success"
                  />
                  <UserStatsCard
                    title="Inactive Users"
                    value={stats?.inactiveUsers}
                    change="-1 this week"
                    changeType="negative"
                    icon="UserX"
                    color="warning"
                  />
                  <UserStatsCard
                    title="Pending Users"
                    value={stats?.pendingUsers}
                    change="3 awaiting approval"
                    changeType="neutral"
                    icon="UserClock"
                    color="error"
                  />
                </div>

                {/* Filters */}
                <UserFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                  onBulkAction={handleBulkAction}
                  selectedUsers={selectedUsers}
                />

                {/* User Table */}
                <UserTable
                  users={filteredUsers}
                  selectedUsers={selectedUsers}
                  onSelectUser={handleSelectUser}
                  onSelectAll={handleSelectAll}
                  onEditUser={handleEditUser}
                  onViewUser={handleViewUser}
                  onResetPassword={handleResetPassword}
                  onToggleStatus={handleToggleStatus}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </>
            ) : (
              <SecurityPanel
                securityData={securityData}
                onViewAuditLog={handleViewAuditLog}
                onViewSessions={handleViewSessions}
              />
            )}
          </div>
        </main>
      </div>
      {/* User Modal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        user={selectedUser}
        onSave={handleSaveUser}
        mode={modalMode}
      />
    </div>
  );
};

export default UserManagement;